/* Klaro Journal — pure logic layer.
 *
 * Ported from the Klaro mobile app so the web journal groups, filters,
 * sorts and merges entries with identical semantics. No DOM access —
 * loadable in Node for parity tests.
 *
 * Source modules (mobile app):
 *   helpers/cigarNotes.js            → notes JSON parsing (klaro_v1/v2)
 *   helpers/cigarDataAdapter.js      → resolveCigarData / normalizeCigarProfile
 *   helpers/itemStorageLocations.js  → signatures + active box summary
 *   helpers/journalEntries.js        → entry factory + grouping
 *   helpers/collectionUIPersistence.js → mergeCollectionUIState
 *   components/Collection/JournalToolbar.js / JournalFilterSheet.js /
 *   JournalTimeFilter.js             → sort/filter/time constants
 *   screens/CollectionScreen.js      → visibleJournalEntries pipeline
 */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) {
    module.exports = factory();
  } else {
    root.JournalLogic = factory();
  }
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  // ── cigarNotes ─────────────────────────────────────────────────────────

  var FLAVOUR_ATTRIBUTES = [
    { label: 'Body', value: 0 },
    { label: 'Linger/Finish', value: 0 },
    { label: 'Oily', value: 0 },
    { label: 'Bitter', value: 0 },
    { label: 'Leather', value: 0 },
    { label: 'Earthy', value: 0 },
    { label: 'Woody', value: 0 },
    { label: 'Nutty', value: 0 },
    { label: 'Chocolate', value: 0 },
    { label: 'Toffee', value: 0 },
    { label: 'Vanilla', value: 0 },
    { label: 'Sweet', value: 0 },
    { label: 'Spicy', value: 0 },
    { label: 'Herbal', value: 0 },
    { label: 'Dark Fruit', value: 0 },
    { label: 'Tropical Fruit', value: 0 },
    { label: 'Mouthfeel', value: 0 },
  ];

  var VALID_NOTES_KEYS = { klaro_v1: true, klaro_v2: true };

  var emptyFlavours = function () {
    return FLAVOUR_ATTRIBUTES.map(function (f) {
      return { label: f.label, value: 0 };
    });
  };

  var hasFlavourSignal = function (flavours) {
    return Array.isArray(flavours) && flavours.some(function (f) {
      return f && Number(f.value) > 0;
    });
  };

  var normalizeFlavours = function (flavours) {
    var source = Array.isArray(flavours) ? flavours : [];
    var valuesByLabel = new Map(
      source
        .filter(function (item) { return item && item.label; })
        .map(function (item) { return [item.label, Number(item.value) || 0]; })
    );
    return emptyFlavours().map(function (item) {
      return { label: item.label, value: valuesByLabel.get(item.label) || 0 };
    });
  };

  var emptyCigarData = function () {
    return {
      maker: '',
      price: '',
      origin: '',
      length: '',
      ringSize: '',
      intensity: '',
      rating: 0,
      ratingMode: '',
      notes: '',
      flavours: emptyFlavours(),
      favorite: false,
      archived: false,
      tags: [],
      addedAt: null,
      updatedAt: null,
    };
  };

  // Mirror of the app's serializeCigarNotes (v2). Used to write the
  // favorite flag back onto app inventory items — the server normalizes
  // incoming payloads to the FULL key set (missing keys become ''), so a
  // partial write would blank the profile; always send everything.
  var serializeCigarNotes = function (data) {
    data = data || {};
    var now = Date.now();
    return JSON.stringify({
      _k: 'klaro_v2',
      maker: data.maker || '',
      price: data.price || '',
      origin: data.origin || '',
      length: data.length || '',
      ringSize: data.ringSize || '',
      intensity: data.intensity || '',
      rating: data.rating || 0,
      ratingMode:
        data.ratingMode === 'average' || data.ratingMode === 'manual'
          ? data.ratingMode
          : '',
      notes: data.notes || '',
      flavours: normalizeFlavours(data.flavours),
      favorite: data.favorite === true,
      archived: data.archived === true,
      tags: Array.isArray(data.tags) ? data.tags : [],
      addedAt: typeof data.addedAt === 'number' ? data.addedAt : now,
      updatedAt: now,
    });
  };

  var parseCigarNotes = function (rawNotes) {
    if (!rawNotes) return emptyCigarData();
    try {
      var parsed = JSON.parse(rawNotes);
      if (parsed && VALID_NOTES_KEYS[parsed._k]) {
        return Object.assign({}, emptyCigarData(), parsed, {
          flavours: normalizeFlavours(parsed.flavours),
          favorite: parsed.favorite === true,
          archived: parsed.archived === true,
          tags: Array.isArray(parsed.tags) ? parsed.tags : [],
          addedAt: typeof parsed.addedAt === 'number' ? parsed.addedAt : null,
          updatedAt: typeof parsed.updatedAt === 'number' ? parsed.updatedAt : null,
          ratingMode:
            parsed.ratingMode === 'average' || parsed.ratingMode === 'manual'
              ? parsed.ratingMode
              : '',
        });
      }
    } catch (e) {
      /* not JSON — fall through to plain text */
    }
    return Object.assign({}, emptyCigarData(), { notes: rawNotes });
  };

  // ── cigarDataAdapter ───────────────────────────────────────────────────

  var pickFirst = function (source, keys) {
    if (!source || typeof source !== 'object') return undefined;
    for (var i = 0; i < keys.length; i++) {
      var key = keys[i];
      if (Object.prototype.hasOwnProperty.call(source, key) && source[key] != null) {
        return source[key];
      }
    }
    return undefined;
  };

  var normalizeTimestamp = function (value) {
    if (value == null || value === '') return null;
    if (typeof value === 'number' && isFinite(value)) {
      return value < 1e12 ? value * 1000 : value;
    }
    if (typeof value === 'string') {
      var parsed = Date.parse(value);
      return isNaN(parsed) ? null : parsed;
    }
    if (value instanceof Date) {
      var millis = value.getTime();
      return isNaN(millis) ? null : millis;
    }
    return null;
  };

  var DIRECT_PROFILE_MARKERS = [
    'maker', 'price', 'origin', 'length', 'ringSize', 'ring_size',
    'intensity', 'rating', 'ratingMode', 'rating_mode', 'favorite',
    'archived', 'tags', 'flavours', 'flavors', 'addedAt', 'added_at',
    'updatedAt', 'updated_at', 'updated_in_ui_at',
  ];

  var hasDirectProfileFields = function (source) {
    if (!source || typeof source !== 'object') return false;
    return DIRECT_PROFILE_MARKERS.some(function (key) {
      return Object.prototype.hasOwnProperty.call(source, key) && source[key] != null;
    });
  };

  var normalizeCigarProfile = function (profile) {
    if (!profile || typeof profile !== 'object') return emptyCigarData();
    var base = emptyCigarData();
    var ratingValue = pickFirst(profile, ['rating']);
    var rawFlavours = pickFirst(profile, ['flavours', 'flavors']);
    var addedAt = normalizeTimestamp(pickFirst(profile, ['addedAt', 'added_at']));
    var updatedAt = normalizeTimestamp(
      pickFirst(profile, ['updatedAt', 'updated_at', 'updated_in_ui_at'])
    );
    var ratingMode = pickFirst(profile, ['ratingMode', 'rating_mode']);
    return Object.assign({}, base, profile, {
      maker: pickFirst(profile, ['maker']) || '',
      price: pickFirst(profile, ['price']) || '',
      origin: pickFirst(profile, ['origin']) || '',
      length: pickFirst(profile, ['length']) || '',
      ringSize: pickFirst(profile, ['ringSize', 'ring_size']) || '',
      intensity: pickFirst(profile, ['intensity']) || '',
      notes: pickFirst(profile, ['notes']) || '',
      favorite: profile.favorite === true,
      archived: profile.archived === true,
      tags: Array.isArray(profile.tags) ? profile.tags : [],
      rating: typeof ratingValue === 'number' ? ratingValue : 0,
      ratingMode: ratingMode === 'average' || ratingMode === 'manual' ? ratingMode : '',
      flavours: normalizeFlavours(rawFlavours),
      addedAt: addedAt,
      updatedAt: updatedAt,
    });
  };

  var resolveCigarData = function (item, profilesById) {
    profilesById = profilesById || {};
    if (!item) return emptyCigarData();
    if (item.cigar_profile && typeof item.cigar_profile === 'object') {
      return normalizeCigarProfile(item.cigar_profile);
    }
    if (item.cigar_profile_id && profilesById && profilesById[item.cigar_profile_id]) {
      return normalizeCigarProfile(profilesById[item.cigar_profile_id]);
    }
    if (hasDirectProfileFields(item)) {
      return normalizeCigarProfile(item);
    }
    return parseCigarNotes(item.notes);
  };

  // ── itemStorageLocations ───────────────────────────────────────────────

  var normalizeText = function (value) {
    return String(value || '').trim().toLowerCase().replace(/\s+/g, ' ');
  };

  var buildStoredCigarSignature = function (item) {
    if (!item) return '';
    var profileId = item.cigar_profile_id != null
      ? item.cigar_profile_id
      : (item.cigar_profile && item.cigar_profile.id);
    if (profileId != null) return 'profile:' + profileId;
    var data = resolveCigarData(item);
    return 'legacy:' + normalizeText(item.name) + '::' + normalizeText(data.maker);
  };

  var buildJournalSnapshotSignature = function (snapshot) {
    snapshot = snapshot || {};
    return 'legacy:' + normalizeText(snapshot.name) + '::' + normalizeText(snapshot.brand);
  };

  var summarizeActiveBoxes = function (locations, preferredBoxName) {
    locations = Array.isArray(locations) ? locations : [];
    preferredBoxName = preferredBoxName || '';
    if (locations.length === 0) return '';

    var normalizedPreferred = normalizeText(preferredBoxName);
    var sorted = locations.slice().sort(function (a, b) {
      return String(a.name || '').localeCompare(String(b.name || ''));
    });
    var preferred = normalizedPreferred
      ? sorted.find(function (location) {
          return normalizeText(location.name) === normalizedPreferred;
        })
      : null;
    var primary = preferred || sorted[0];
    if (!primary || !primary.name) return '';

    var quantity = Number(primary.quantity || 0);
    var quantityLabel =
      quantity > 0 ? ' · ' + quantity + ' ' + (quantity === 1 ? 'stick' : 'sticks') : '';
    var otherCount = sorted.length - 1;
    if (otherCount <= 0) return primary.name + quantityLabel;
    return (
      primary.name + quantityLabel + ' + ' + otherCount + ' more ' +
      (otherCount === 1 ? 'box' : 'boxes')
    );
  };

  // ── journalEntries ─────────────────────────────────────────────────────

  var generateEntryId = function () {
    return (
      'je_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 8)
    );
  };

  var createJournalEntry = function (params) {
    params = params || {};
    var item = params.item;
    var boxName = params.boxName || '';
    var storageUnitId = params.storageUnitId != null ? params.storageUnitId : null;
    var storageUnitName = params.storageUnitName || '';
    var rating = params.rating || 0;
    var notes = params.notes || '';
    var contextNotes = params.contextNotes || '';
    var tagIds = params.tagIds || [];
    var occurredAt = params.occurredAt;
    var reducedQty = params.reducedQty === true;
    var reducedQtyCount = params.reducedQtyCount || 0;
    var snapshotName = params.snapshotName || '';
    var snapshotBrand = params.snapshotBrand || '';
    // Web-only overrides: the app derives these from the linked item, but
    // free-form web entries have no item to inherit from.
    var snapshotOrigin = params.snapshotOrigin || '';
    var snapshotStrength = params.snapshotStrength || '';

    var rich = item ? resolveCigarData(item) : {};
    var now = Date.now();
    var normalizedReducedQtyCount = reducedQty ? Math.max(1, reducedQtyCount || 1) : 0;
    var resolvedStorageUnitId =
      storageUnitId != null
        ? storageUnitId
        : (item && (item.__storageUnitId != null
            ? item.__storageUnitId
            : item.storage_unit_id != null
              ? item.storage_unit_id
              : item.humidor_id != null
                ? item.humidor_id
                : null)) || null;
    var resolvedStorageUnitName =
      storageUnitName ||
      boxName ||
      (item && (item.__storageUnitName || item.humidor_name)) ||
      '';

    return {
      id: generateEntryId(),
      itemId: item && item.id != null ? item.id : null,
      storageUnitId: resolvedStorageUnitId,
      storageUnitName: resolvedStorageUnitName,
      humidorId: resolvedStorageUnitId,
      humidorName: resolvedStorageUnitName,
      createdAt: now,
      updatedAt: now,
      occurredAt: typeof occurredAt === 'number' ? occurredAt : now,
      rating: Math.max(0, Math.min(5, rating | 0)),
      notes: (notes || '').trim(),
      contextNotes: (contextNotes || '').trim(),
      tagIds: Array.isArray(tagIds) ? tagIds : [],
      reducedQty: normalizedReducedQtyCount > 0,
      reducedQtyCount: normalizedReducedQtyCount,
      snapshot: {
        name: snapshotName || ((item && item.name) || ''),
        brand: snapshotBrand || rich.maker || '',
        origin: snapshotOrigin || rich.origin || '',
        strength: snapshotStrength || rich.intensity || '',
        boxName: resolvedStorageUnitName,
        storageUnitId: resolvedStorageUnitId,
        storageUnitName: resolvedStorageUnitName,
        humidorId: resolvedStorageUnitId,
        humidorName: resolvedStorageUnitName,
      },
    };
  };

  // Web-only: a "stick" added on the web journal has no inventory item to
  // live on (storage units require real hardware), so it persists as a seed
  // journal entry. Shape mirrors the app's derived initial-rating entries so
  // the app renders the group naturally; snapshot carries superset keys
  // (length, ringSize, price) the app preserves but doesn't display yet.
  var createStickSeedEntry = function (params) {
    params = params || {};
    var name = (params.name || '').trim();
    var brand = (params.brand || '').trim();
    var now = Date.now();
    var occurredAt = typeof params.occurredAt === 'number' ? params.occurredAt : now;
    var rating = Math.max(0, Math.min(5, (params.rating || 0) | 0));
    var groupKey = normalizeText(name) + '::' + normalizeText(brand);
    return {
      id: 'initial_rating_web_' + groupKey.replace(/[^a-z0-9]+/g, '_') + '_' + now.toString(36),
      itemId: null,
      storageUnitId: null,
      storageUnitName: '',
      humidorId: null,
      humidorName: '',
      createdAt: now,
      updatedAt: now,
      occurredAt: occurredAt,
      rating: rating,
      notes: (params.notes || '').trim(),
      contextNotes: '',
      tagIds: Array.isArray(params.tagIds) ? params.tagIds : [],
      reducedQty: false,
      reducedQtyCount: 0,
      initialRating: true,
      fromOrder: params.fromOrder === true,
      snapshot: {
        name: name,
        brand: brand,
        origin: (params.origin || '').trim(),
        strength: params.strength || '',
        length: (params.length || '').trim(),
        ringSize: (params.ringSize || '').trim(),
        price: (params.price || '').trim(),
        // Web-only: { url?, key?, source: 'store'|'order'|'upload' } —
        // Apple's rules keep photos out of the app, but the app preserves
        // the key through its merges so web photos survive round-trips.
        image: params.image || null,
        // Web-only: store pack pricing [{ label, price }] from the linked
        // catalog product (5 Pack / Full Box / …). Same superset deal.
        prices: Array.isArray(params.prices) && params.prices.length ? params.prices : null,
        // Web-only: linked catalog product handle — detail view fetches the
        // store's tasting description live from it (never persisted).
        storeHandle: params.storeHandle || null,
        flavours: normalizeFlavours(params.flavours || []),
        quantity: Math.max(0, parseInt(params.quantity, 10) || 0),
        favorite: params.favorite === true,
        boxName: '',
        storageUnitId: null,
        storageUnitName: '',
        humidorId: null,
        humidorName: '',
      },
    };
  };

  var sortEntriesByOccurredDesc = function (entries) {
    if (!Array.isArray(entries)) return [];
    return entries.slice().sort(function (a, b) {
      return (b.occurredAt || 0) - (a.occurredAt || 0);
    });
  };

  var toTimestamp = function (value) {
    if (typeof value === 'number' && isFinite(value)) return value;
    if (typeof value === 'string') {
      var parsed = Date.parse(value);
      if (isFinite(parsed)) return parsed;
    }
    return null;
  };

  var getJournalItemTimestamp = function (item, rich) {
    rich = rich || resolveCigarData(item);
    return (
      toTimestamp(item && item.updated_at) ||
      toTimestamp(item && item.updatedAt) ||
      toTimestamp(rich && rich.updatedAt) ||
      toTimestamp(item && item.created_at) ||
      toTimestamp(item && item.createdAt) ||
      toTimestamp(rich && rich.addedAt) ||
      Date.now()
    );
  };

  var getInitialRatingTimestamp = function (item, rich) {
    rich = rich || resolveCigarData(item);
    return (
      toTimestamp(item && item.created_at) ||
      toTimestamp(item && item.createdAt) ||
      toTimestamp(rich && rich.addedAt) ||
      toTimestamp(item && item.updated_at) ||
      toTimestamp(item && item.updatedAt) ||
      toTimestamp(rich && rich.updatedAt) ||
      Date.now()
    );
  };

  var normalizeGroupKey = function (value) {
    return String(value || '').trim().toLowerCase().replace(/\s+/g, ' ');
  };

  var legacyGroupKey = function (params) {
    params = params || {};
    return (
      'legacy:' + normalizeGroupKey(params.name) + '::' + normalizeGroupKey(params.brand)
    );
  };

  var getJournalGroupKeyForItem = function (item) {
    if (!item) return '';
    var profileId = item.cigar_profile_id != null
      ? item.cigar_profile_id
      : (item.cigar_profile && item.cigar_profile.id);
    if (profileId != null) return 'profile:' + profileId;
    var rich = resolveCigarData(item);
    return (
      buildStoredCigarSignature(item) ||
      legacyGroupKey({ name: item.name, brand: rich.maker })
    );
  };

  var getJournalGroupKeyForEntry = function (entry, inventoryByItemId) {
    inventoryByItemId = inventoryByItemId || new Map();
    var itemId = entry && (entry.itemId != null ? entry.itemId : entry.stickId);
    var inventoryMatch = itemId != null ? inventoryByItemId.get(String(itemId)) : null;
    if (inventoryMatch && inventoryMatch.item) {
      return getJournalGroupKeyForItem(inventoryMatch.item);
    }
    return (
      buildJournalSnapshotSignature((entry && entry.snapshot) || {}) ||
      legacyGroupKey({
        name: entry && entry.snapshot && entry.snapshot.name,
        brand: entry && entry.snapshot && entry.snapshot.brand,
      })
    );
  };

  var createInitialRatingEntryForItem = function (params) {
    params = params || {};
    var item = params.item;
    var boxName = params.boxName || '';
    var storageUnitId = params.storageUnitId != null ? params.storageUnitId : null;
    if (!item) return null;
    var rich = resolveCigarData(item);
    var rating = Math.max(0, Math.min(5, Number(rich.rating || 0)));
    if (rating <= 0) return null;

    var timestamp = getInitialRatingTimestamp(item, rich);
    var itemId = item.id != null ? item.id : null;
    return {
      id:
        'initial_rating_' + (storageUnitId || 'unit') + '_' +
        (itemId || getJournalGroupKeyForItem(item)),
      itemId: itemId,
      storageUnitId: storageUnitId,
      storageUnitName: boxName || '',
      humidorId: storageUnitId,
      humidorName: boxName || '',
      createdAt: timestamp,
      updatedAt: timestamp,
      occurredAt: timestamp,
      rating: rating,
      notes: (rich.notes || '').trim(),
      contextNotes: '',
      tagIds: [],
      reducedQty: false,
      reducedQtyCount: 0,
      synthetic: true,
      initialRating: true,
      snapshot: {
        name: item.name || '',
        brand: rich.maker || '',
        origin: rich.origin || '',
        strength: rich.intensity || '',
        boxName: boxName || '',
        storageUnitId: storageUnitId,
        storageUnitName: boxName || '',
        humidorId: storageUnitId,
        humidorName: boxName || '',
      },
    };
  };

  var getAverageEntryRating = function (entries) {
    entries = entries || [];
    var ratings = entries
      .map(function (entry) { return Number((entry && entry.rating) || 0); })
      .filter(function (rating) { return rating > 0; });
    if (ratings.length === 0) return 0;
    var average =
      ratings.reduce(function (sum, rating) { return sum + rating; }, 0) / ratings.length;
    return Math.round(average * 10) / 10;
  };

  var entriesForJournalGroup = function (entries, group) {
    entries = Array.isArray(entries) ? entries : [];
    if (!group) return [];
    var itemIds = new Set(
      [group.itemId].concat((group.items || []).map(function (item) { return item && item.id; }))
        .filter(function (id) { return id != null; })
        .map(String)
    );
    var groupKey = group.groupKey || '';
    var liveMatches = entries.filter(function (entry) {
      var entryItemId = entry && (entry.itemId != null ? entry.itemId : entry.stickId);
      if (entryItemId != null && itemIds.has(String(entryItemId))) return true;
      return groupKey && getJournalGroupKeyForEntry(entry) === groupKey;
    });
    if (liveMatches.length > 0) return liveMatches;
    return Array.isArray(group.entries) ? group.entries : [];
  };

  var getJournalDisplayTitle = function (entry) {
    if (!entry) return 'Untitled entry';
    var itemWithName = (entry.items || []).find(function (item) {
      return item && item.name;
    });
    var candidates = [
      entry.primaryItem && entry.primaryItem.name,
      entry.snapshot && entry.snapshot.name,
      entry.name,
      itemWithName && itemWithName.name,
    ];
    var title = candidates.find(function (value) {
      return String(value || '').trim();
    });
    return String(title || 'Untitled entry').trim();
  };

  var buildJournalCigarGroups = function (params) {
    params = params || {};
    var journalEntries = params.journalEntries || [];
    var inventoryRecords = params.inventoryRecords || [];
    var groups = new Map();
    var inventoryByItemId = new Map();

    var ensureGroup = function (key, snapshot) {
      snapshot = snapshot || {};
      if (!key) return null;
      if (!groups.has(key)) {
        groups.set(key, {
          id: 'journal_group_' + key,
          groupKey: key,
          itemId: null,
          primaryItem: null,
          primaryStorageUnitId: null,
          items: [],
          entries: [],
          activeLocations: [],
          itemRatings: [],
          initialRatingEntry: null,
          itemNotes: [],
          favorite: false,
          createdAt: 0,
          occurredAt: 0,
          snapshot: {
            name: snapshot.name || '',
            brand: snapshot.brand || '',
            origin: snapshot.origin || '',
            strength: snapshot.strength || '',
            length: snapshot.length || '',
            ringSize: snapshot.ringSize || '',
            price: snapshot.price || '',
            image: snapshot.image || null,
            prices: snapshot.prices || null,
            storeHandle: snapshot.storeHandle || null,
            flavours: normalizeFlavours(snapshot.flavours || []),
            boxName: snapshot.boxName || '',
            quantity: Number(snapshot.quantity || 0),
          },
        });
      }
      var group = groups.get(key);
      group.snapshot = Object.assign({}, group.snapshot, {
        name: group.snapshot.name || snapshot.name || '',
        brand: group.snapshot.brand || snapshot.brand || '',
        origin: group.snapshot.origin || snapshot.origin || '',
        strength: group.snapshot.strength || snapshot.strength || '',
        length: group.snapshot.length || snapshot.length || '',
        ringSize: group.snapshot.ringSize || snapshot.ringSize || '',
        price: group.snapshot.price || snapshot.price || '',
        image: group.snapshot.image || snapshot.image || null,
        prices: group.snapshot.prices || snapshot.prices || null,
        storeHandle: group.snapshot.storeHandle || snapshot.storeHandle || null,
        flavours: hasFlavourSignal(group.snapshot.flavours)
          ? group.snapshot.flavours
          : normalizeFlavours(snapshot.flavours || []),
        boxName: group.snapshot.boxName || snapshot.boxName || '',
        quantity: Math.max(
          Number(group.snapshot.quantity || 0),
          Number(snapshot.quantity || 0)
        ),
      });
      return group;
    };

    inventoryRecords.forEach(function (record) {
      var item = (record && record.item) || record;
      if (!item || !item.name) return;
      var rich = resolveCigarData(item);
      var key = getJournalGroupKeyForItem(item);
      var boxName = (record && record.boxName) || '';
      var quantity = Number(item.quantity || 0);
      var timestamp = getJournalItemTimestamp(item, rich);
      var group = ensureGroup(key, {
        name: item.name,
        brand: rich.maker || '',
        origin: rich.origin || '',
        strength: rich.intensity || '',
        length: rich.length || '',
        ringSize: rich.ringSize || '',
        price: rich.price || '',
        image: rich.image || null,
        prices: rich.prices || null,
        storeHandle: rich.storeHandle || null,
        flavours: rich.flavours,
        quantity: quantity,
        boxName: boxName,
      });
      if (!group) return;

      if (item.id != null) inventoryByItemId.set(String(item.id), record);
      group.items.push(item);
      if (
        !group.primaryItem ||
        quantity > Number((group.primaryItem && group.primaryItem.quantity) || 0)
      ) {
        group.primaryItem = item;
        group.itemId = item.id;
        group.primaryStorageUnitId =
          (record && record.storageUnit && record.storageUnit.id) != null
            ? record.storageUnit.id
            : (record && record.storageUnitId) != null
              ? record.storageUnitId
              : null;
      }
      if (Number(rich.rating || 0) > 0) {
        group.itemRatings.push(Number(rich.rating));
        var initialEntry = createInitialRatingEntryForItem({
          item: item,
          boxName: boxName,
          storageUnitId:
            (record && record.storageUnit && record.storageUnit.id) != null
              ? record.storageUnit.id
              : (record && record.storageUnitId) != null
                ? record.storageUnitId
                : null,
        });
        if (
          initialEntry &&
          (!group.initialRatingEntry ||
            (initialEntry.occurredAt || 0) > (group.initialRatingEntry.occurredAt || 0))
        ) {
          group.initialRatingEntry = initialEntry;
        }
      }
      if ((rich.notes || '').trim()) group.itemNotes.push((rich.notes || '').trim());
      if (rich.favorite === true) group.favorite = true;
      group.createdAt = group.createdAt || timestamp;
      group.occurredAt = Math.max(group.occurredAt || 0, timestamp);

      var locationId =
        (record && record.storageUnit && record.storageUnit.id) != null
          ? record.storageUnit.id
          : (record && record.storageUnitId) != null
            ? record.storageUnitId
            : boxName;
      if (
        quantity > 0 &&
        boxName &&
        !group.activeLocations.some(function (location) {
          return String(location.id) === String(locationId);
        })
      ) {
        group.activeLocations.push({ id: locationId, name: boxName, quantity: quantity });
      }
    });

    journalEntries.forEach(function (entry) {
      var key = getJournalGroupKeyForEntry(entry, inventoryByItemId);
      var snapshot = (entry && entry.snapshot) || {};
      var group = ensureGroup(key, snapshot);
      if (!group) return;
      if (snapshot.favorite === true) group.favorite = true;
      group.entries.push(entry);
      group.createdAt = group.createdAt || entry.createdAt || entry.occurredAt || Date.now();
      group.occurredAt = Math.max(
        group.occurredAt || 0,
        entry.occurredAt || entry.createdAt || Date.now()
      );
    });

    return Array.from(groups.values()).map(function (group) {
      var explicitAverage = getAverageEntryRating(group.entries);
      var itemAverage = getAverageEntryRating(
        group.itemRatings.map(function (rating) { return { rating: rating }; })
      );
      var allEntries = group.initialRatingEntry
        ? [group.initialRatingEntry].concat(group.entries)
        : group.entries;
      var rating = getAverageEntryRating(allEntries) || itemAverage || 0;
      var latestEntry = sortEntriesByOccurredDesc(allEntries)[0];
      var historyCount = allEntries.length;
      var activeLocationLabel = summarizeActiveBoxes(
        group.activeLocations,
        group.snapshot.boxName
      );

      return Object.assign({}, group, {
        entries: allEntries,
        synthetic: true,
        journalGroup: true,
        rating: rating,
        averageRating: explicitAverage || 0,
        manualRating: itemAverage || 0,
        historyCount: historyCount,
        notes: (latestEntry && latestEntry.notes) || group.itemNotes[0] || '',
        contextNotes: (latestEntry && latestEntry.contextNotes) || '',
        activeLocationLabel: activeLocationLabel,
        snapshot: Object.assign({}, group.snapshot, {
          boxName:
            group.snapshot.boxName ||
            (group.activeLocations[0] && group.activeLocations[0].name) ||
            '',
        }),
      });
    });
  };

  var notesPreview = function (notes, maxLen) {
    maxLen = maxLen || 140;
    if (!notes) return null;
    var flat = notes.replace(/\s+/g, ' ').trim();
    if (!flat) return null;
    if (flat.length <= maxLen) return flat;
    return flat.slice(0, maxLen).replace(/\s+$/, '') + '…';
  };

  var formatEntryDate = function (ms) {
    if (!ms) return '';
    var d = new Date(ms);
    var now = new Date();
    var sameDay = function (a, b) {
      return (
        a.getFullYear() === b.getFullYear() &&
        a.getMonth() === b.getMonth() &&
        a.getDate() === b.getDate()
      );
    };
    var yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);
    if (sameDay(d, now)) return 'Today';
    if (sameDay(d, yesterday)) return 'Yesterday';
    var monthName = d.toLocaleString('en-US', { month: 'short' });
    var dayNum = d.getDate();
    if (d.getFullYear() === now.getFullYear()) return monthName + ' ' + dayNum;
    return monthName + ' ' + dayNum + ', ' + d.getFullYear();
  };

  // ── collectionUIPersistence (merge semantics) ──────────────────────────

  var newerTimestamp = function (entry) {
    return (
      Number((entry && (entry.updatedAt || entry.createdAt || entry.occurredAt)) || 0) || 0
    );
  };

  var mergeById = function (base, incoming) {
    base = base || [];
    incoming = incoming || [];
    var merged = new Map();
    base.concat(incoming).forEach(function (entry) {
      if (!entry || !entry.id) return;
      var existing = merged.get(entry.id);
      if (!existing || newerTimestamp(entry) >= newerTimestamp(existing)) {
        merged.set(entry.id, Object.assign({}, existing, entry));
      }
    });
    return Array.from(merged.values());
  };

  var mergeUniqueStrings = function (base, incoming, limit) {
    base = base || [];
    incoming = incoming || [];
    limit = limit || 5;
    var seen = new Set();
    return incoming.concat(base)
      .filter(function (entry) {
        var value = String(entry || '').trim();
        if (!value || seen.has(value)) return false;
        seen.add(value);
        return true;
      })
      .slice(0, limit);
  };

  var mergeCollectionUIState = function (base, incoming) {
    base = base || {};
    incoming = incoming || {};
    return Object.assign({}, base, incoming, {
      perStorageUnit: Object.assign(
        {},
        base.perStorageUnit || {},
        incoming.perStorageUnit || {}
      ),
      recentSearches: mergeUniqueStrings(base.recentSearches, incoming.recentSearches),
      tagLibrary: mergeById(base.tagLibrary, incoming.tagLibrary),
      savedViews: mergeById(base.savedViews, incoming.savedViews),
      journalEntries: mergeById(base.journalEntries, incoming.journalEntries),
    });
  };

  // ── constants: sort / filters / time ranges / view modes ───────────────

  var VIEW_MODES = { COMPACT: 'compact', COMFORTABLE: 'comfortable', GRID: 'grid' };
  var DEFAULT_JOURNAL_VIEW_MODE = VIEW_MODES.COMFORTABLE;

  var JOURNAL_SORT_OPTIONS = [
    { key: 'name', dir: 'asc', label: 'Name A–Z' },
    { key: 'name', dir: 'desc', label: 'Name Z–A' },
    { key: 'date', dir: 'desc', label: 'Newest' },
    { key: 'date', dir: 'asc', label: 'Oldest' },
    { key: 'rating', dir: 'desc', label: 'Rating ↓' },
    { key: 'rating', dir: 'asc', label: 'Rating ↑' },
    { key: 'entries', dir: 'desc', label: 'Most entries' },
    { key: 'entries', dir: 'asc', label: 'Fewest entries' },
  ];

  var DEFAULT_JOURNAL_SORT = { key: 'name', dir: 'asc' };

  var DEFAULT_JOURNAL_FILTERS = {
    favoritesOnly: false,
    ratingMin: 0,
    ratingOp: 'all',
    boxes: [],
    hasNotes: false,
    dateRange: 'all',
    minEntryCount: 0,
  };

  var ENTRY_COUNT_OPTIONS = [
    { value: 0, label: 'All' },
    { value: 1, label: '1+' },
    { value: 2, label: '2+' },
    { value: 3, label: '3+' },
    { value: 5, label: '5+' },
  ];

  var countJournalFilters = function (filters) {
    if (!filters) return 0;
    var n = 0;
    if (filters.favoritesOnly) n++;
    if (filters.ratingOp && filters.ratingOp !== 'all') n++;
    if (filters.boxes && filters.boxes.length > 0) n++;
    if (filters.hasNotes) n++;
    if (filters.dateRange && filters.dateRange !== 'all') n++;
    if (Number(filters.minEntryCount || 0) > 0) n++;
    return n;
  };

  var JOURNAL_TIME_RANGES = [
    // All-time sits last but stays the default selection.
    { key: 'week', label: 'Week' },
    { key: 'month', label: 'Month' },
    { key: 'year', label: 'Year' },
    { key: 'all', label: 'All-time' },
  ];

  var getTimeRangeLowerBound = function (key) {
    var now = new Date();
    switch (key) {
      case 'week': {
        var d = new Date(now);
        d.setDate(now.getDate() - 7);
        return d.getTime();
      }
      case 'month':
        return new Date(now.getFullYear(), now.getMonth(), 1).getTime();
      case 'year':
        return new Date(now.getFullYear(), 0, 1).getTime();
      case 'all':
      default:
        return null;
    }
  };

  // Filter-sheet dateRange facet → lower bound (rolling windows).
  var getDateRangeLowerBound = function (key) {
    var DAY = 86400000;
    switch (key) {
      case '7d': return Date.now() - 7 * DAY;
      case '30d': return Date.now() - 30 * DAY;
      case '90d': return Date.now() - 90 * DAY;
      case '1y': return Date.now() - 365 * DAY;
      case 'all':
      default: return null;
    }
  };

  // ── visibleJournalEntries pipeline (CollectionScreen port) ─────────────
  //
  // inventoryRecords: [{ item, storageUnit, storageUnitId, boxName }]
  // Returns filtered + sorted journal cigar groups.

  var computeVisibleGroups = function (params) {
    params = params || {};
    var journalEntries = params.journalEntries || [];
    var inventoryRecords = params.inventoryRecords || [];
    var timeRange = params.timeRange || 'all';
    var search = params.search || '';
    var filters = params.filters || DEFAULT_JOURNAL_FILTERS;
    var sort = params.sort || DEFAULT_JOURNAL_SORT;

    var groups = buildJournalCigarGroups({
      journalEntries: journalEntries,
      inventoryRecords: inventoryRecords,
    });
    var bound = getTimeRangeLowerBound(timeRange);
    var facetBound = getDateRangeLowerBound(filters.dateRange);
    if (facetBound != null && (bound == null || facetBound > bound)) bound = facetBound;
    var q = (search || '').trim().toLowerCase();
    var f = filters;

    var list = groups.filter(function (group) {
      var hasExplicitEntryInRange =
        bound == null ||
        (group.entries || []).some(function (entry) {
          return (entry.occurredAt || 0) >= bound;
        });
      if (!hasExplicitEntryInRange && bound != null && (group.occurredAt || 0) < bound) {
        return false;
      }
      if (q) {
        var haystack = [
          group.notes || '',
          (group.snapshot && group.snapshot.name) || '',
          (group.snapshot && group.snapshot.boxName) || '',
          (group.snapshot && group.snapshot.brand) || '',
          group.activeLocationLabel || '',
        ]
          .concat((group.entries || []).map(function (entry) { return entry.notes || ''; }))
          .join(' ')
          .toLowerCase();
        if (haystack.indexOf(q) === -1) return false;
      }
      if (f.favoritesOnly && group.favorite !== true) return false;
      if (f.ratingOp && f.ratingOp !== 'all') {
        var target = Number(f.ratingMin || 0);
        var groupRating = Number(group.rating || 0);
        if (f.ratingOp === 'lt' && groupRating >= target) return false;
        if (f.ratingOp === 'gt' && groupRating <= target) return false;
        if (f.ratingOp === 'eq' && Math.round(groupRating) !== target) return false;
      } else if (f.ratingMin && (group.rating || 0) < f.ratingMin) {
        return false;
      }
      if (f.boxes && f.boxes.length > 0) {
        var names = [(group.snapshot && group.snapshot.boxName) || ''].concat(
          (group.activeLocations || []).map(function (location) {
            return location.name || '';
          })
        );
        var matchesBox = names.some(function (name) {
          return f.boxes.indexOf(name) !== -1;
        });
        if (!matchesBox) return false;
      }
      if (Number(f.minEntryCount || 0) > 0) {
        if (Number(group.historyCount || 0) < Number(f.minEntryCount || 0)) return false;
      }
      if (f.hasNotes && !(group.notes && group.notes.trim().length > 0)) return false;
      return true;
    });

    var dir = sort.dir === 'asc' ? 1 : -1;
    if (sort.key === 'rating') {
      list = list.slice().sort(function (a, b) {
        return ((a.rating || 0) - (b.rating || 0)) * dir;
      });
    } else if (sort.key === 'entries') {
      list = list.slice().sort(function (a, b) {
        var countDelta = ((a.historyCount || 0) - (b.historyCount || 0)) * dir;
        if (countDelta !== 0) return countDelta;
        return ((a.occurredAt || 0) - (b.occurredAt || 0)) * -1;
      });
    } else if (sort.key === 'name') {
      list = list.slice().sort(function (a, b) {
        var an = getJournalDisplayTitle(a).toLowerCase();
        var bn = getJournalDisplayTitle(b).toLowerCase();
        if (an < bn) return -1 * dir;
        if (an > bn) return 1 * dir;
        return 0;
      });
    } else {
      list = list.slice().sort(function (a, b) {
        return ((a.occurredAt || 0) - (b.occurredAt || 0)) * dir;
      });
    }
    return list;
  };

  var availableJournalBoxes = function (journalEntries, inventoryRecords) {
    var set = new Set();
    (journalEntries || []).forEach(function (e) {
      if (e && e.snapshot && e.snapshot.boxName) set.add(e.snapshot.boxName);
    });
    (inventoryRecords || []).forEach(function (record) {
      if (record && record.boxName) set.add(record.boxName);
    });
    return Array.from(set).sort();
  };

  // ── tag helpers ────────────────────────────────────────────────────────

  var MAX_TAG_LIBRARY_SIZE = 50;
  var MAX_TAGS_PER_ENTRY = 8;
  // First swatch of the app's TAG_COLOR_PALETTE — default for new tags.
  var DEFAULT_TAG_COLOR = '#FF611B';

  var generateTagId = function () {
    return 'tag_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 8);
  };

  // App's INTENSITY_OPTIONS (constants/collectionUI.js) — values stored on
  // item.intensity / entry snapshot.strength.
  var INTENSITY_OPTIONS = ['Mild', 'Mild-Medium', 'Medium', 'Medium-Full', 'Full'];

  return {
    FLAVOUR_ATTRIBUTES: FLAVOUR_ATTRIBUTES,
    emptyFlavours: emptyFlavours,
    normalizeFlavours: normalizeFlavours,
    hasFlavourSignal: hasFlavourSignal,
    emptyCigarData: emptyCigarData,
    parseCigarNotes: parseCigarNotes,
    serializeCigarNotes: serializeCigarNotes,
    normalizeCigarProfile: normalizeCigarProfile,
    resolveCigarData: resolveCigarData,
    normalizeText: normalizeText,
    buildStoredCigarSignature: buildStoredCigarSignature,
    buildJournalSnapshotSignature: buildJournalSnapshotSignature,
    summarizeActiveBoxes: summarizeActiveBoxes,
    createJournalEntry: createJournalEntry,
    createStickSeedEntry: createStickSeedEntry,
    sortEntriesByOccurredDesc: sortEntriesByOccurredDesc,
    getJournalItemTimestamp: getJournalItemTimestamp,
    createInitialRatingEntryForItem: createInitialRatingEntryForItem,
    getAverageEntryRating: getAverageEntryRating,
    getJournalGroupKeyForItem: getJournalGroupKeyForItem,
    getJournalGroupKeyForEntry: getJournalGroupKeyForEntry,
    entriesForJournalGroup: entriesForJournalGroup,
    getJournalDisplayTitle: getJournalDisplayTitle,
    buildJournalCigarGroups: buildJournalCigarGroups,
    notesPreview: notesPreview,
    formatEntryDate: formatEntryDate,
    newerTimestamp: newerTimestamp,
    mergeById: mergeById,
    mergeUniqueStrings: mergeUniqueStrings,
    mergeCollectionUIState: mergeCollectionUIState,
    VIEW_MODES: VIEW_MODES,
    DEFAULT_JOURNAL_VIEW_MODE: DEFAULT_JOURNAL_VIEW_MODE,
    JOURNAL_SORT_OPTIONS: JOURNAL_SORT_OPTIONS,
    DEFAULT_JOURNAL_SORT: DEFAULT_JOURNAL_SORT,
    DEFAULT_JOURNAL_FILTERS: DEFAULT_JOURNAL_FILTERS,
    ENTRY_COUNT_OPTIONS: ENTRY_COUNT_OPTIONS,
    countJournalFilters: countJournalFilters,
    JOURNAL_TIME_RANGES: JOURNAL_TIME_RANGES,
    getTimeRangeLowerBound: getTimeRangeLowerBound,
    getDateRangeLowerBound: getDateRangeLowerBound,
    computeVisibleGroups: computeVisibleGroups,
    availableJournalBoxes: availableJournalBoxes,
    MAX_TAG_LIBRARY_SIZE: MAX_TAG_LIBRARY_SIZE,
    MAX_TAGS_PER_ENTRY: MAX_TAGS_PER_ENTRY,
    DEFAULT_TAG_COLOR: DEFAULT_TAG_COLOR,
    generateTagId: generateTagId,
    INTENSITY_OPTIONS: INTENSITY_OPTIONS,
  };
});
